import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtTons2, fmtPct, fmtRate, fmtDay, addDays, monthStart } from '../components/PostCollections/format';
import { c, btn, card, tbl } from '../theme';

// Post Collections — Daily Productivity Report: the MRF Daily Production
// Report the Tableau workbook draws, for Irwindale and Sun Valley.
//
// Every figure is computed on the server (services/postCollectionsMetrics.js,
// checked value for value against the workbook) — the page lays numbers out
// and does no arithmetic, so a tile and the table cannot disagree.
//
// The columns follow the workbook's own order, grouped. Sites do not all have
// the same columns: the optional figures (the inbound breakdown, processable
// MSW, organics to Crown) appear only where the site's rules define them, and
// the server says which (`columns`).

const GROUPS = [
  { label: 'Tons in', cols: [
    { key: 'inbound_tons', label: 'Inbound' },
    { key: 'inbound_msw', label: 'MSW', optional: true },
    { key: 'inbound_comm_organics', label: 'Comm. organics', optional: true },
    { key: 'inbound_resi_gw', label: 'Resi GW', optional: true },
  ] },
  { label: 'Processing', cols: [
    { key: 'tons_on_floor', label: 'Tons on floor' },
    { key: 'processable_recycle', label: 'Processable recycle' },
    { key: 'processable_msw', label: 'Processable MSW', optional: true },
    { key: 'non_processable', label: 'Non-processable' },
    { key: 'total_tons_processed', label: 'Total processed', strong: true },
    { key: 'pct_processed', label: '% of processable', kind: 'pct' },
  ] },
  { label: 'Lines and labor', cols: [
    { key: 'production_hours', label: 'Production hrs' },
    { key: 'sorter_hours', label: 'Sorter hrs' },
    { key: 'uptime', label: 'Uptime', kind: 'pct' },
  ] },
  { label: 'Positive recovery', cols: [
    { key: 'scrap_metal', label: 'Scrap metal' },
    { key: 'glass', label: 'Glass' },
    { key: 'aluminum_cans', label: 'Aluminum cans' },
    { key: 'metals', label: 'Metals' },
    { key: 'occ', label: 'OCC' },
    { key: 'mixed_paper', label: 'Mixed paper' },
    { key: 'plastic', label: 'Plastic' },
    { key: 'positive_diversion_tons', label: 'Total', strong: true },
    { key: 'positive_diversion_pct', label: '% of processed', kind: 'pct' },
    { key: 'positive_tons_per_sorter_hour', label: 'Tons / sorter hr', kind: 'rate' },
  ] },
  { label: 'Negative recovery', cols: [
    { key: 'wood', label: 'Wood' },
    { key: 'organics_to_crown', label: 'Organics to Crown', optional: true },
    { key: 'fines', label: 'Fines' },
    { key: 'total_processed_diversion', label: 'Total diversion', strong: true },
    { key: 'processed_diversion_pct', label: 'Diversion %', kind: 'pct' },
  ] },
];

const fmt = (kind, v) => (kind === 'pct' ? fmtPct(v === null || v === undefined ? v : v * 100) : kind === 'rate' ? fmtRate(v) : fmtTons2(v));

export default function PostCollections() {
  const [chosen, setSite] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');

  const sitesQ = useQuery({ queryKey: ['post-collections-sites'], queryFn: () => api.get('/post-collections/sites') });
  const sites = sitesQ.data || [];
  // Until somebody picks, the first site the report offers.
  const site = chosen || sites[0]?.code || '';
  const current = sites.find((x) => x.code === site);
  const latest = current?.last_day || '';

  const params = useMemo(() => {
    const p = { site };
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [site, from, to]);

  const reportQ = useQuery({
    queryKey: ['post-collections-report', params],
    queryFn:  () => api.get('/post-collections/report', { params }),
    enabled:  sitesQ.isSuccess && !!site,
    placeholderData: (prev) => prev,
  });
  const data = reportQ.data;

  function preset(kind) {
    if (!latest) return;
    if (kind === 'day') { setFrom(latest); setTo(latest); }
    if (kind === 'month') { setFrom(monthStart(latest)); setTo(latest); }
    if (kind === 'prev') {
      const end = addDays(monthStart(latest), -1);
      setFrom(monthStart(end)); setTo(end);
    }
  }

  if (sitesQ.isLoading) return <div style={s.page}><LoadingSpinner /></div>;

  const shownFrom = from || data?.from || '';
  const shownTo   = to || data?.to || '';
  const cols = GROUPS.map((g) => ({ ...g, cols: g.cols.filter((col) => !col.optional || (data?.columns || []).includes(col.key)) }))
    .filter((g) => g.cols.length);
  const anyData = sites.some((x) => x.days > 0);

  const controls = (
    <div style={s.controls}>
      <div role="group" aria-label="Site" style={s.segment}>
        {sites.map((x) => (
          <button key={x.code} type="button" aria-pressed={site === x.code}
                  onClick={() => { setSite(x.code); setFrom(''); setTo(''); }}
                  style={{ ...s.segBtn, ...(site === x.code ? s.segOn : null) }}>{x.name}</button>
        ))}
      </div>
      <div style={s.presets} role="group" aria-label="Range">
        <button type="button" style={s.presetBtn} onClick={() => preset('day')}>Latest day</button>
        <button type="button" style={s.presetBtn} onClick={() => preset('month')}>Month to date</button>
        <button type="button" style={s.presetBtn} onClick={() => preset('prev')}>Last month</button>
      </div>
      <label style={s.pickLabel}>
        <span style={s.pickCaption}>From</span>
        <input id="pc-from" type="date" aria-label="From" value={shownFrom} max={shownTo || undefined}
               onChange={(e) => { setFrom(e.target.value); if (!to) setTo(shownTo); }} style={s.pick} />
      </label>
      <label style={s.pickLabel}>
        <span style={s.pickCaption}>To</span>
        <input id="pc-to" type="date" aria-label="To" value={shownTo} min={shownFrom || undefined}
               onChange={(e) => { setTo(e.target.value); if (!from) setFrom(shownFrom); }} style={s.pick} />
      </label>
    </div>
  );

  const rangeText = data?.from ? (data.from === data.to ? fmtDay(data.from, { weekday: true }) : `${fmtDay(data.from)} – ${fmtDay(data.to)}`) : '';

  return (
    <div style={s.page}>
      <style>{css}</style>
      <PageHeader
        eyebrow="Post Collections"
        title="Daily Productivity Report"
        subtitle={rangeText ? `${current?.name || ''} · ${rangeText}` : 'Tons processed, recovery and diversion by day, by site.'}
        actions={sites.length ? controls : null}
        alignActions="end"
      />

      {!anyData ? (
        <div style={s.emptyCard}>
          Nothing has been loaded yet. A super admin loads the daily export under Data Uploads → Post Collections Upload.
        </div>
      ) : reportQ.isLoading ? <LoadingSpinner /> : reportQ.error ? (
        <div style={s.error} role="alert">{reportQ.error.message}</div>
      ) : data?.empty ? (
        <div style={s.emptyCard}>Nothing has been loaded for {data.name} yet.</div>
      ) : data ? (
        <>
          <Notes data={data} />
          <Tiles t={data.total} />

          <section style={s.card}>
            <div className="pc-scroll">
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.groupTh, ...s.stickyHead }} rowSpan={2}>Date</th>
                    {cols.map((g) => <th key={g.label} colSpan={g.cols.length} style={s.groupTh}>{g.label}</th>)}
                  </tr>
                  <tr>
                    {cols.flatMap((g) => g.cols.map((col, i) => (
                      <th key={col.key} style={{ ...s.thR, ...(i === 0 ? s.groupEdge : null) }}>{col.label}</th>
                    )))}
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((r) => (
                    <tr key={r.day} style={r.has_data ? tbl.tr : s.trEmpty}>
                      <td style={{ ...s.td, ...s.stickyCell }}>{fmtDay(r.day, { weekday: true })}</td>
                      {cols.flatMap((g) => g.cols.map((col, i) => (
                        <td key={col.key} style={{ ...s.tdR, ...(i === 0 ? s.groupEdge : null), ...(col.strong ? s.strong : null) }}
                            title={col.key === 'total_tons_processed' ? marks(r) : undefined}>
                          {r.has_data ? fmt(col.kind, r[col.key]) : '—'}
                          {col.key === 'total_tons_processed' && r.prev_tof_estimated && <sup style={s.mark}>†</sup>}
                          {col.key === 'total_tons_processed' && r.msw_shifts > 1 && r.processable_msw ? <sup style={s.mark}>‡</sup> : null}
                        </td>
                      )))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...s.tdTotal, ...s.stickyCell, ...s.stickyTotal }}>Total</td>
                    {cols.flatMap((g) => g.cols.map((col, i) => (
                      <td key={col.key} style={{ ...s.tdTotalR, ...(i === 0 ? s.groupEdge : null) }}>{fmt(col.kind, data.total[col.key])}</td>
                    )))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
          <p style={s.footnote}>
            Tons are scale-ticket tons; baled commodities are bale counts × the site&apos;s average weight per bale.
            Totals add the days; percentages in the total row are of the totals, and uptime is the average of the days that report it.
          </p>
        </>
      ) : null}
    </div>
  );
}

function marks(r) {
  const out = [];
  if (r.prev_tof_estimated) out.push('† Uses an estimated previous-day tons on floor: this day was loaded from a file without PrevTOF.');
  if (r.msw_shifts > 1 && r.processable_msw) out.push(`‡ Processable MSW counted once per end-of-shift entry (${r.msw_shifts}), as the Tableau report does.`);
  return out.join('\n') || undefined;
}

function Notes({ data }) {
  const notes = [];
  if (!data.weights.loaded) {
    notes.push('No bale weights are loaded, so aluminum cans, metals, OCC, mixed paper and plastic read as zero. A super admin uploads them on the Post Collections Upload page.');
  } else if (data.weights.missing.length) {
    notes.push(`No bale weight is loaded for ${data.weights.missing.join(', ')} at this site; those bales read as zero tons.`);
  }
  if (data.estimated_prev_tof_days.length) {
    notes.push(`† ${data.estimated_prev_tof_days.length} day(s) were loaded from a file without PrevTOF, so Total Tons Processed uses the prior day's highest tons on floor. Re-upload those days from the export with PrevTOF to replace the estimate.`);
  }
  if (data.two_shift_msw_days.length) {
    notes.push(`‡ ${data.two_shift_msw_days.map((d) => fmtDay(d)).join(', ')}: two end-of-shift entries, so processable MSW is counted twice in Total Tons Processed, matching the Tableau report.`);
  }
  if (!notes.length) return null;
  return (
    <div style={s.note} role="note">
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
      <ul style={s.noteList}>{notes.map((n) => <li key={n}>{n}</li>)}</ul>
    </div>
  );
}

function Tiles({ t }) {
  return (
    <div style={s.tiles}>
      <Tile label="Total tons processed" value={fmtTons2(t.total_tons_processed)}
            sub={t.pct_processed === null ? 'Nothing processable' : `${fmtPct(t.pct_processed * 100)} of processable tons`} />
      <Tile label="Positive recovery" value={fmtTons2(t.positive_diversion_tons)}
            sub={`${fmtPct(t.positive_diversion_pct * 100)} of tons processed`} />
      <Tile label="Tons per sorter hour" value={fmtRate(t.positive_tons_per_sorter_hour)}
            sub={`${fmtTons2(t.sorter_hours)} sorter hours`} />
      <Tile label="Uptime" value={t.uptime === null ? '—' : fmtPct(t.uptime * 100)}
            sub={`${fmtTons2(t.production_hours)} production hours`} />
      <Tile label="Total diversion" value={fmtPct(t.processed_diversion_pct * 100)}
            sub={`${fmtTons2(t.total_processed_diversion)} tons, positive and negative`} />
      <Tile label="Inbound tons" value={fmtTons2(t.inbound_tons)} sub={`${t.days} day${t.days === 1 ? '' : 's'} with data`} />
    </div>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue}>{value}</div>
      <div style={s.tileSub}>{sub}</div>
    </div>
  );
}

// The table is wider than any screen, so it scrolls inside its card and the
// date column stays put. Real CSS: sticky positioning needs the scroll
// container to be the overflow parent, and an inline style cannot set that
// up across the table's descendants.
const css = `
.pc-scroll { overflow: auto; max-height: 70vh; }
.pc-scroll thead th { position: sticky; top: 0; z-index: 2; }
.pc-scroll thead tr:nth-child(2) th { top: 31px; }
.pc-scroll tfoot td { position: sticky; bottom: 0; z-index: 2; }
`;

const s = {
  page:        { maxWidth: '1600px', margin: '0 auto', paddingBottom: '8px' },
  controls:    { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  segment:     { display: 'flex', border: `1px solid ${c.border}`, borderRadius: '6px', overflow: 'hidden', height: '32px' },
  segBtn:      { border: 'none', background: c.surface, color: c.textStrong, fontSize: '12.5px', fontFamily: 'inherit', padding: '0 12px', cursor: 'pointer' },
  segOn:       { background: c.navy, color: c.onNavy },
  presets:     { display: 'flex', gap: '6px' },
  presetBtn:   { ...btn.secondary, height: '32px' },
  pickLabel:   { display: 'flex', flexDirection: 'column', gap: '3px' },
  pickCaption: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textFaint },
  pick:        { height: '32px', fontSize: '12.5px', fontFamily: 'inherit', padding: '0 8px', border: `1px solid ${c.border}`, borderRadius: '6px', background: c.surface, color: c.text, cursor: 'pointer' },
  tiles:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '14px' },
  tile:        { ...card.sm, padding: '14px 16px' },
  tileLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  tileValue:   { fontSize: '24px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums', marginTop: '4px' },
  tileSub:     { fontSize: '11.5px', color: c.textMuted, marginTop: '3px', lineHeight: 1.4 },
  card:        { ...card.sm, overflow: 'hidden', marginBottom: '10px' },
  table:       { borderCollapse: 'separate', borderSpacing: 0, fontSize: '12px', minWidth: '100%' },
  groupTh:     { ...tbl.thSm, background: c.surfaceSunk, textAlign: 'center', borderLeft: `1px solid ${c.border}`, height: '31px', boxSizing: 'border-box' },
  thR:         { ...tbl.thRSm, background: c.surfaceSunk, whiteSpace: 'normal', minWidth: '78px', verticalAlign: 'bottom', borderBottom: `1px solid ${c.border}` },
  groupEdge:   { borderLeft: `1px solid ${c.border}` },
  stickyHead:  { position: 'sticky', left: 0, zIndex: 3, textAlign: 'left', borderLeft: 'none', borderBottom: `1px solid ${c.border}` },
  stickyCell:  { position: 'sticky', left: 0, zIndex: 1, background: c.surface, borderRight: `1px solid ${c.border}` },
  stickyTotal: { zIndex: 3, background: c.surfaceSunk },
  td:          { ...tbl.tdSm, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  tdR:         { ...tbl.tdRSm, borderBottom: `1px solid ${c.canvas}` },
  trEmpty:     { color: c.textFaint, background: c.surfaceSunk },
  strong:      { fontWeight: 600 },
  tdTotal:     { ...tbl.tdSm, fontWeight: 700, background: c.surfaceSunk, borderTop: `2px solid ${c.borderStrong}`, whiteSpace: 'nowrap' },
  tdTotalR:    { ...tbl.tdRSm, fontWeight: 700, background: c.surfaceSunk, borderTop: `2px solid ${c.borderStrong}` },
  mark:        { color: c.warningText, marginLeft: '1px' },
  note:        { display: 'flex', gap: '8px', background: c.warningTint, border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px', color: c.warningText },
  noteList:    { margin: 0, paddingLeft: '16px', lineHeight: 1.6 },
  footnote:    { fontSize: '11.5px', color: c.textMuted, margin: '0 0 14px', lineHeight: 1.5 },
  emptyCard:   { ...card.sm, padding: '56px 24px', textAlign: 'center', color: c.textMuted, fontSize: '13px' },
  error:       { padding: '16px', color: c.dangerText, fontSize: '12.5px' },
};
